import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { createContext, useState, useContext, useMemo, useRef } from "react";
import { pipe, toUpper, compose, keys, path, pluck, equals } from "ramda";
import cookies from "js-cookie";
import querystring from "query-string";
import { selectUnitListWithUnits, repairUnitListsProps, repairSelectedUnitList, newUnitListEntry } from "@unitLists/utils";
import { sortUnits } from "utils/sort";
import { useCustomEffect } from "hooks";
import { useOrganizationsContext } from "@webapi/contexts/organizations";
import { jsx as _jsx } from "react/jsx-runtime";
export var UnitsListsContext = /*#__PURE__*/createContext({});
export var UnitsListsContextProvider = function UnitsListsContextProvider(props) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    defaultUnitListsLoaded = _useState2[0],
    setDefaultUnitListsLoaded = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    defaultRegions = _useState4[0],
    setDefaultRegions = _useState4[1];
  var _useOrganizationsCont = useOrganizationsContext(),
    regions = _useOrganizationsCont.regions;
  var query = querystring.parse(location.search);
  var _originalLists = useMemo(function () {
    var cookieUnitLists = cookies.get("visUnitLists");
    if (typeof cookieUnitLists === "string") {
      cookieUnitLists = compose(JSON.parse, path(["0"]), keys, querystring.parse)(cookieUnitLists);
      cookieUnitLists = cookieUnitLists.map(function (list) {
        return _objectSpread(_objectSpread({}, list), {}, {
          // mark all lists that are in users cookies as permanent
          // no volatile lists should make it to the cookie anymore
          volatile: false
        });
      });
      return cookieUnitLists;
    }
    return [].concat(newUnitListEntry([], [], true, true));
  }, []);
  var queryUnits = query.units && [].concat(query.units).map(toUpper);
  var cleanedNewLists = useMemo(function () {
    return queryUnits || _originalLists ? pipe(function (_queryUnits) {
      return _queryUnits ? selectUnitListWithUnits(_originalLists, _queryUnits) : _originalLists;
    }, repairUnitListsProps, repairSelectedUnitList, function (lists) {
      return lists.map(function (list) {
        return _objectSpread(_objectSpread({}, list), {}, {
          units: list.units || []
        });
      });
    })(queryUnits) : [];
  }, [queryUnits, _originalLists]);
  var _useState5 = useState(cleanedNewLists),
    _useState6 = _slicedToArray(_useState5, 2),
    unitLists = _useState6[0],
    setUnitLists = _useState6[1];
  var cleanedPreviousLists = repairUnitListsProps(_originalLists);
  var prevUnitLists = useRef(cleanedPreviousLists);
  useCustomEffect(function () {
    if (!equals(prevUnitLists.current, unitLists)) {
      prevUnitLists.current = unitLists;
      cookies.set("visUnitLists",
      // ensure no volatile lists are saved
      JSON.stringify(unitLists.filter(function (list) {
        return !list.volatile;
      })), {
        expires: 1000,
        secure: true
      });
    }
  }, [unitLists]);

  // This hook is used to set the default unit lists on page load when
  // there are no units in the query string and no unit list is selected
  // Since we only need it on page load, we use the defaultUnitListsLoaded state
  // to ensure it runs only once, we skip it if there are units in the query string
  // or default regions are not yet available (they need to be fetched first)
  useCustomEffect(function () {
    var sortedDefaultRegions = (regions === null || regions === void 0 ? void 0 : regions.length) > 0 ? sortUnits(["SE"].concat(_toConsumableArray(pluck("id", regions)))) : [];
    if (defaultUnitListsLoaded || !sortedDefaultRegions.length) return;

    // ensure that units present in the query string
    // take precedence over default regions
    var newQueryUnits = queryUnits || sortedDefaultRegions;
    var cleanedNewLists = function cleanedNewLists() {
      return newQueryUnits || _originalLists ? pipe(function (_queryUnits) {
        return _queryUnits ? selectUnitListWithUnits(_originalLists, _queryUnits) : _originalLists;
      }, repairUnitListsProps, repairSelectedUnitList, function (lists) {
        return lists.map(function (list) {
          return _objectSpread(_objectSpread({}, list), {}, {
            units: list.units || []
          });
        });
      })(newQueryUnits) : [];
    };
    setUnitLists(cleanedNewLists);
    setDefaultUnitListsLoaded(true);
    setDefaultRegions(sortedDefaultRegions);
  }, [defaultUnitListsLoaded, regions]);
  var activeUnitList = unitLists.find(function (x) {
    return x.selected;
  });
  return /*#__PURE__*/_jsx(UnitsListsContext.Provider, _objectSpread({
    value: useMemo(function () {
      return {
        activeUnitList: activeUnitList,
        unitLists: unitLists,
        setUnitLists: setUnitLists,
        defaultUnitListsLoaded: defaultUnitListsLoaded,
        defaultRegions: defaultRegions
      };
    }, [activeUnitList, unitLists, setUnitLists, defaultUnitListsLoaded, defaultRegions])
  }, props));
};
export var useUnitListsContext = function useUnitListsContext() {
  return useContext(UnitsListsContext);
};